/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { Box, Container, Typography, Tabs, Tab } from '@mui/material';
import { SmartToy } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useTeamsApps } from '../../hooks/useAssistant';
import { TeamsAppsTab } from './Tabs/TeamsAppsTab';
import { downloadTeamsAppPackage } from '../../apis/teamsApps';
import { downloadBlobFile } from '../../services/downloadService';
import DialogLoader from '../common/DialogLoader';

// Lazy load the dialog components
const ManageApp = lazy(() =>
    import('./Dialogs/ManageApp').then((module) => ({
        default: module.ManageApp,
    })),
);

const EditAppDialog = lazy(() =>
    import('./Dialogs/EditApp').then((module) => ({
        default: module.EditAppDialog,
    })),
);

const DeleteAppDialog = lazy(() =>
    import('./Dialogs/DeleteApp').then((module) => ({
        default: module.DeleteAppDialog,
    })),
);

const TeamsAppSettingsDialog = lazy(() =>
    import('./Dialogs/TeamsAppSettings').then((module) => ({
        default: module.TeamsAppSettingsDialog,
    })),
);

const TeamsAppInfoDialog = lazy(() =>
    import('./Dialogs/TeamsAppInfo').then((module) => ({
        default: module.TeamsAppInfoDialog,
    })),
);

// Extract constants
const INITIAL_PAGINATION_STATE = {
    page: 0,
    pageSize: 5,
};

export const TeamsApps = () => {
    const [downloading, setDownloading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [assignAssistantDialog, setAssignAssistantDialog] = useState(false);
    const [selectedAppId, setSelectedAppId] = useState(null);
    const [selectedApp, setSelectedApp] = useState(null);
    const [appsPaginationModel, setAppsPaginationModel] = useState(INITIAL_PAGINATION_STATE);

    const [editAppDialog, setEditAppDialog] = useState(false);
    const [deleteAppDialog, setDeleteAppDialog] = useState(false);
    const [teamsAppSettingsDialog, setTeamsAppSettingsDialog] = useState(false);
    const [teamsAppInfoDialog, setTeamsAppInfoDialog] = useState(false);

    const { teamsAppsList, loading: teamsAppsLoading, getTeamsAppsList, totalCount: teamsAppsTotalCount } = useTeamsApps();

    // For ManageApp: derive selected app from list so it updates after getTeamsAppsList()
    const selectedAppForManage = useMemo(
        () => teamsAppsList?.find((app) => app.id === selectedAppId) ?? null,
        [teamsAppsList, selectedAppId],
    );

    // Fetch teams apps when pagination changes
    useEffect(() => {
        getTeamsAppsList(appsPaginationModel.page + 1, appsPaginationModel.pageSize);
    }, [appsPaginationModel.page, appsPaginationModel.pageSize, getTeamsAppsList]);

    const handleAssignCourses = useCallback((app) => {
        setSelectedAppId(app?.id ?? null);
        setAssignAssistantDialog(true);
    }, []);

    const handleTabChange = useCallback((_event, newValue) => {
        setActiveTab(newValue);
    }, []);

    // Consolidated function to close dialog and reset selected app
    const resetDialogAndSelection = useCallback(() => {
        setSelectedApp(null);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setAssignAssistantDialog(false);
        setSelectedAppId(null);
        resetDialogAndSelection();
    }, [resetDialogAndSelection]);

    const handleRefreshList = useCallback(() => {
        getTeamsAppsList(appsPaginationModel.page + 1, appsPaginationModel.pageSize);
    }, [getTeamsAppsList, appsPaginationModel.page, appsPaginationModel.pageSize]);

    const handleEdit = useCallback((app) => {
        setSelectedApp(app);
        setEditAppDialog(true);
    }, []);

    const handleCloseEditDialog = useCallback(() => {
        setEditAppDialog(false);
        resetDialogAndSelection();
    }, [resetDialogAndSelection]);

    const handleDelete = useCallback((app) => {
        setSelectedApp(app);
        setDeleteAppDialog(true);
    }, []);

    const handleCloseDeleteDialog = useCallback(() => {
        setDeleteAppDialog(false);
        resetDialogAndSelection();
    }, [resetDialogAndSelection]);

    const handleZipDownload = useCallback(
        async (app) => {
            if (downloading) return; // Prevent multiple simultaneous downloads

            if (!app?.id) {
                toast.error('App information is missing. Please try again.');
                return;
            }

            setDownloading(true);
            try {
                // Call the API endpoint to download the zip file
                const response = await downloadTeamsAppPackage(app.id);

                // Create a blob from the response and trigger download
                const blob = new Blob([response.data], { type: 'application/zip' });
                const filename = app?.name ? `${app.name.replace(/\s+/g, '-')}.zip` : 'xTract2AI.zip';
                downloadBlobFile(blob, filename);

                toast.success('Package downloaded successfully');
            } catch (error) {
                console.error('Error downloading zip file:', error);
                const errorMessage =
                    error?.response?.data?.message || error?.message || 'Failed to download package. Please try again.';
                toast.error(errorMessage);
            } finally {
                setDownloading(false);
            }
        },
        [downloading],
    );

    const handleTeamsAppSettings = useCallback(() => {
        setTeamsAppSettingsDialog(true);
    }, []);

    const handleCloseTeamsAppSettingsDialog = useCallback(() => {
        setTeamsAppSettingsDialog(false);
    }, []);

    const handleCloseTeamsAppInfoDialog = useCallback(() => {
        setTeamsAppInfoDialog(false);
    }, []);

    return (
        <>
            <Container className="custom_table_container ai-integration-container" maxWidth={false}>
                {/* Header Section */}
                <Box variant="div" className="table_filters ai-integration-header">
                    <Typography variant="h6" fontWeight={600}>
                        Apps
                    </Typography>
                </Box>

                {/* Tabs Section */}
                <Box className="ai-integration-tabs-wrapper">
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        className="ai-integration-tabs"
                        aria-label="teams apps tabs"
                    >
                        <Tab
                            label="MS Teams"
                            icon={
                                <SmartToy
                                    className={`ai-integration-tab-icon ${
                                        activeTab === 0
                                            ? 'ai-integration-tab-icon-active'
                                            : 'ai-integration-tab-icon-inactive'
                                    }`}
                                />
                            }
                            iconPosition="start"
                            className="ai-integration-tab"
                        />
                    </Tabs>
                </Box>

                {/* Tab Panel Content */}
                <Box role="tabpanel" aria-labelledby="teams-tab">
                    {activeTab === 0 && (
                        <TeamsAppsTab
                            teamsAppsList={teamsAppsList || []}
                            loading={teamsAppsLoading}
                            paginationModel={appsPaginationModel}
                            onPaginationModelChange={setAppsPaginationModel}
                            totalCount={teamsAppsTotalCount}
                            downloading={downloading}
                            onDownload={handleZipDownload}
                            onAssignCourses={handleAssignCourses}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            setTeamsAppInfoDialog={setTeamsAppInfoDialog}
                            handleTeamsAppSettings={handleTeamsAppSettings}
                        />
                    )}
                </Box>
            </Container>

            {/* Lazy loaded dialog - only loads when dialog state becomes true */}
            {assignAssistantDialog && (
                <Suspense fallback={<DialogLoader />}>
                    <ManageApp
                        open={assignAssistantDialog}
                        onClose={handleCloseDialog}
                        selectedApp={selectedAppForManage}
                        getTeamsAppsList={handleRefreshList}
                        link={selectedAppForManage?.teams_app_link}
                    />
                </Suspense>
            )}

            {editAppDialog && (
                <Suspense fallback={<DialogLoader />}>
                    <EditAppDialog
                        open={editAppDialog}
                        onClose={handleCloseEditDialog}
                        onSuccess={handleRefreshList}
                        selectedApp={selectedApp}
                    />
                </Suspense>
            )}

            {deleteAppDialog && (
                <Suspense fallback={<DialogLoader />}>
                    <DeleteAppDialog
                        open={deleteAppDialog}
                        onClose={handleCloseDeleteDialog}
                        onSuccess={handleRefreshList}
                        selectedApp={selectedApp}
                    />
                </Suspense>
            )}

            {teamsAppSettingsDialog && (
                <Suspense fallback={<DialogLoader />}>
                    <TeamsAppSettingsDialog open={teamsAppSettingsDialog} onClose={handleCloseTeamsAppSettingsDialog} />
                </Suspense>
            )}

            {teamsAppInfoDialog && (
                <Suspense fallback={<DialogLoader />}>
                    <TeamsAppInfoDialog open={teamsAppInfoDialog} onClose={handleCloseTeamsAppInfoDialog} />
                </Suspense>
            )}
        </>
    );
};

export default TeamsApps;
